import axios from "axios";
import Vue from "vue";
import { DateTime } from "luxon";

const state = {
  /**
   * Vybrané vozidlo
   */
  vehicle: null,
  /**
   * Plál servisů pro dané vozidlo
   */
  plans: null,
  /**
   * Aktuálně vybraný plán servisů pro dané vozidlo
   */
  plan: null,
  /**
   * Servisy pro vybarné obodbí
   */
  services: null,

  fetching: false,
  fetched: null
};

const getters = {};

const actions = {
  clear({ commit, state }) {
    if (state.fetching)
      return Promise.resolve();

    if (state.vehicel !== null || state.plans !== null || state.plan !== null) {
      commit("fetched", {
        vehicle: null,
        plans: null,
        plan: null,
        services: null
      });
    }
  },

  refresh({ state, dispatch }) {
    return dispatch("fetch", { planId: (state.plan !== null ? state.servicePlanId : null), refresh: true });
  },

  /**
   * Stáhne servisy vodzila podle zadaných parametrů.
   * Parametry: {
   *   vehicle: string,     - objekt vozidla
   *   periodFrom: string,  - datum a čas od
   *   periodTo: string,    - datum a čas do
   *   paramCodes: string[] - parametry ke stažení
   *  }
   *
   * @param params Parametry
   */
  fetch({ commit, state, dispatch }, params) {
    // pokud se vozidla právě vyčítaji, nic nedělěj
    if (state.fetching)
      return Promise.resolve();

    // nastavime, ze se objekt s touto URL uz stahuje, aby se to neptalo nekolikrat
    commit("fetching");

    try {
      if (params.vehicle === null || params.vehicle === undefined) {
        if (state.vehicle !== null)
          // pokud není vozidlo zadáno použij stávající (ale pouze pokud je zadáno)
          params.vehicle = state.vehicle;
        else {
          commit("fetched", {});
          return Promise.resolve();
        }
      }

      var change = (state.vehicle === null || state.vehicle === undefined || state.vehicle.vehicleId !== params.vehicle.vehicleId || params.refresh);
      var plans = null;
      var plan = null;
      var services = null;
      let tid;

      if (params.vehicle === null) {
        if (state.vehicle !== null) {
          // pokud není vozidlo zadáno použij stávající (ale pouze pokud je zadáno)
          params.vehicle = state.vehicle;
        } else {
          commit("fetched", {});
          return Promise.resolve();
        }
      }

      return dispatch("progress/add", "Fetch record", { root: true })
        .then(id => {
          tid = id;
          // načti jízdy pouze pokud se změnilo období
          if (change || params.refresh) {
            change = true;

            // nacti měření
            return axios.get(`/v1/{#company#}/service-plan?vehicle=${params.vehicle.vehicleId}&size=500`)
              .then((response) => {
                plans = response.data.content;
                return Promise.resolve();
              }).catch((error) => {
                commit("fetched", {});
                console.log("Vehicle plans fetch error", error);
                return undefined;
              });
          } else {
            plans = state.plans;
            return Promise.resolve();
          }
        })
        .then(() => {
          if (change || params.planId !== undefined && (state.plan === null || state.plan.servicePlanId !== params.planId)) {
            plan = plans.find(x => x.servicePlanId === params.planId) || null;

            if (plan !== null) {
              return axios.get(`/v1/{#company#}/service?plan=${plan.servicePlanId}&size=1000`)
                .then((response) => {
                  services = response.data.content;
                  return Promise.resolve();
                }).catch((error) => {
                  commit("fetched", {});
                  console.log("Vehicle plans fetch error", error);
                  return undefined;
                });
            } else {
              return axios.get(`/v1/{#company#}/service?vehicle=${params.vehicle.vehicleId}&size=1000`)
                .then((response) => {
                  services = response.data.content;
                  return Promise.resolve();
                }).catch((error) => {
                  commit("fetched", {});
                  console.log("Vehicle plans fetch error", error);
                  return undefined;
                });
            }
          }

          return Promise.resolve();
        })
        .then(() => {
          commit("fetched", {
            query: params,
            vehicle: params.vehicle,
            plans: plans,
            plan: plan,
            services: services
          });

          // zruš předchozí mód
          this.dispatch("online/mapMode", "service");

          return Promise.resolve();
        })
        .catch(x => {
          console.log("Fetch error" + x);
        })
        .finally(() => {
          commit("finished");
          dispatch("progress/remove", tid, { root: true });
        });
    } catch {
      commit("finished");
      return Promise.resolve();
    }
  },

  getPlan({ dispatch }, servicePlanId) {
    let tid;
    return dispatch("progress/add", "Get service plan", { root: true })
      .then(id => {
        tid = id;

        return axios.get(`/v1/{#company#}/service-plan/${servicePlanId}`);
      }).then((response) => {
        return response.data;
      }).catch((error) => {
        console.log("get service plan error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  storePlan({ dispatch }, params) {
    let tid;
    return dispatch("progress/add", "Store service plan", { root: true })
      .then(id => {
        tid = id;

        if (params.plan.servicePlanId === undefined || params.plan.servicePlanId === null)
          return axios.post(`/v1/{#company#}/service-plan`, params.plan);
        else
          return axios.put(`/v1/{#company#}/service-plan/${params.plan.servicePlanId}`, params.plan);
      }).then(() => {
        dispatch("refresh");
        return { error: false };
      }).catch((error) => {
        console.log("update service plan error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  deletePlan({ dispatch }, params) {
    let tid;
    return dispatch("progress/add", "Delete plan", { root: true })
      .then(id => {
        tid = id;

        return axios.delete(`/v1/{#company#}/service-plan/${params.servicePlanId}?withServices=${(params.withServices || false) ? 'true' : 'false'}`);
      }).then(() => {
        dispatch("refresh");
        return Promise.resolve();
      }).catch((error) => {
        console.log("delete service plan error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  getService({ dispatch }, serviceId) {
    let tid;
    return dispatch("progress/add", "Get service", { root: true })
      .then(id => {
        tid = id;

        return axios.get(`/v1/{#company#}/service/${serviceId}`);
      }).then((response) => {
        return response.data;
      }).catch((error) => {
        console.log("get service error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  storeService({ dispatch }, params) {
    let tid;
    return dispatch("progress/add", "Store service", { root: true })
      .then(id => {
        tid = id;

        if (params.service.serviceId === undefined || params.service.serviceId === null)
          return axios.post(`/v1/{#company#}/service`, params.service);
        else
          return axios.put(`/v1/{#company#}/service/${params.service.serviceId}`, params.service);
      }).then(() => {
        dispatch("refresh");
        return { error: false };
      }).catch((error) => {
        console.log("update service error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  },

  deleteService({ dispatch }, params) {
    let tid;
    return dispatch("progress/add", "Delete service", { root: true })
      .then(id => {
        tid = id;

        return axios.delete(`/v1/{#company#}/service/${params.serviceId}`);
      }).then(() => {
        dispatch("refresh");
        return Promise.resolve();
      }).catch((error) => {
        console.log("delete service error", error);
        if (error.response)
          return { error: true, message: error.response.data.error };
        else
          return { error: true };
      }).finally(() => dispatch("progress/remove", tid, { root: true }));
  }
};

const mutations = {
  fetched(state, params) {
    Vue.set(state, "query", params.query || null);
    Vue.set(state, "vehicle", params.vehicle || null);
    Vue.set(state, "plans", params.plans || null);
    Vue.set(state, "plan", params.plan || null);
    Vue.set(state, "services", params.services || null);
  },

  /**
   * Nastaví začátek stahování
   */
  fetching(state) {
    Vue.set(state, "fetching", true);
  },

  finished(state) {
    Vue.set(state, "fetching", false);
    Vue.set(state, "fetched", DateTime.now());
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};